import { useState, useEffect, useCallback, useRef } from 'react';
import { track } from '../lib/telemetry';

export type NetworkState = 'online' | 'slow' | 'offline';

export interface NetworkStatus {
  /** Current network state: online, slow, or offline */
  state: NetworkState;
  /** True when state is 'online' or 'slow' (connected but possibly degraded) */
  isOnline: boolean;
  /** True when state is 'slow' */
  isSlow: boolean;
  /** True when state is 'offline' */
  isOffline: boolean;
  /** Timestamp of last known online state */
  lastOnline: number | null;
}

// Threshold for considering a connection "slow" (3 seconds)
const SLOW_THRESHOLD_MS = 3000;

// Number of recent request timings to track for averaging
const TIMING_HISTORY_SIZE = 3;

/**
 * Hook for detecting network connection state.
 * Uses navigator.onLine and tracks request timings to detect slow connections.
 *
 * @param registerTimingCallback - Function to register the timing callback globally
 */
export function useNetworkStatus(
  registerTimingCallback?: (callback: (durationMs: number) => void) => void
): NetworkStatus {
  const [state, setState] = useState<NetworkState>(() =>
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'online'
  );
  const [lastOnline, setLastOnline] = useState<number | null>(() =>
    typeof navigator !== 'undefined' && navigator.onLine ? Date.now() : null
  );

  // Track recent request timings for averaging
  const timingsRef = useRef<number[]>([]);

  // Handle request timing reports
  const handleRequestTiming = useCallback((durationMs: number) => {
    // Don't update timing if we're offline
    if (state === 'offline') return;

    // Add new timing to history
    timingsRef.current.push(durationMs);

    // Keep only the last N timings
    if (timingsRef.current.length > TIMING_HISTORY_SIZE) {
      timingsRef.current.shift();
    }

    // Calculate average
    const average =
      timingsRef.current.reduce((a, b) => a + b, 0) / timingsRef.current.length;

    // Update state based on average timing
    if (average > SLOW_THRESHOLD_MS) {
      setState('slow');
    } else if (state === 'slow') {
      // Only switch back to online if we have enough samples
      if (timingsRef.current.length >= TIMING_HISTORY_SIZE) {
        setState('online');
      }
    }

    // Update last online timestamp
    setLastOnline(Date.now());
  }, [state]);

  // Register the timing callback
  useEffect(() => {
    if (registerTimingCallback) {
      registerTimingCallback(handleRequestTiming);
    }
  }, [registerTimingCallback, handleRequestTiming]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setState('online');
      setLastOnline(Date.now());
      // Reset timing history when coming back online
      timingsRef.current = [];
      track('online_changed', { online: true });
    };

    const handleOffline = () => {
      setState('offline');
      // Don't clear lastOnline - keep the last known online time
      track('online_changed', { online: false });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    state,
    isOnline: state !== 'offline',
    isSlow: state === 'slow',
    isOffline: state === 'offline',
    lastOnline,
  };
}
